import type { CytoscapeElement } from '../types/graph';

export const MAX_COUNTERPARTIES_PER_DIRECTION = 50;

/**
 * Prune excessive leaf nodes per direction from the graph.
 *
 * Only targets leaf nodes (degree === 1) — trunk paths (multi-hop) are never pruned.
 * Gold-path edges are always kept. Remaining edges are sorted by value descending
 * so the highest-value counterparties stay visible on canvas.
 *
 * Pruned leaves are replaced with a single synthetic "[ + N More ]" group node + edge.
 */
export function pruneExcessiveLeaves(elements: CytoscapeElement[], maxLimit = MAX_COUNTERPARTIES_PER_DIRECTION): CytoscapeElement[] {
    const nodes = elements.filter(el => !el.data.source);
    const edges = elements.filter(el => !!el.data.source && !!el.data.target);

    // Build degree map — only leaf nodes (degree === 1) are candidates for pruning
    const nodeDegree = new Map<string, number>();
    nodes.forEach(n => nodeDegree.set(n.data.id, 0));

    edges.forEach(e => {
        const src = e.data.source!;
        const tgt = e.data.target!;
        nodeDegree.set(src, (nodeDegree.get(src) || 0) + 1);
        nodeDegree.set(tgt, (nodeDegree.get(tgt) || 0) + 1);
    });

    // Collect leaf edges per central node, per direction
    const incomingLeafEdges = new Map<string, CytoscapeElement[]>();
    const outgoingLeafEdges = new Map<string, CytoscapeElement[]>();

    edges.forEach(e => {
        const src = e.data.source!;
        const tgt = e.data.target!;

        // Source is a leaf sending TO the central node (incoming to tgt)
        if (nodeDegree.get(src) === 1) {
            if (!incomingLeafEdges.has(tgt)) incomingLeafEdges.set(tgt, []);
            incomingLeafEdges.get(tgt)!.push(e);
        }

        // Target is a leaf receiving FROM the central node (outgoing from src)
        if (nodeDegree.get(tgt) === 1) {
            if (!outgoingLeafEdges.has(src)) outgoingLeafEdges.set(src, []);
            outgoingLeafEdges.get(src)!.push(e);
        }
    });

    const elementsToRemove = new Set<string>();
    const syntheticElements: CytoscapeElement[] = [];

    const processEdges = (
        edgeMap: Map<string, CytoscapeElement[]>,
        isIncoming: boolean
    ) => {
        edgeMap.forEach((leafEdges, centralNodeId) => {
            if (leafEdges.length <= maxLimit) return;

            // Sort: gold-path edges first, then by value descending.
            // This ensures the highest-value counterparties are kept on canvas.
            leafEdges.sort((a, b) => {
                const aGold = a.data.edgeType === 'gold-path' ? 1 : 0;
                const bGold = b.data.edgeType === 'gold-path' ? 1 : 0;
                if (aGold !== bGold) return bGold - aGold; // gold first
                const aVal = Number(a.data.value ?? a.data.total_value ?? 0) || 0;
                const bVal = Number(b.data.value ?? b.data.total_value ?? 0) || 0;
                return bVal - aVal; // highest value first
            });

            // Keep the first maxLimit, prune the rest
            const prunedEdges = leafEdges.slice(maxLimit);
            const prunedCount = prunedEdges.length;

            prunedEdges.forEach(e => {
                elementsToRemove.add(e.data.id);
                const leafNodeId = isIncoming ? e.data.source! : e.data.target!;
                elementsToRemove.add(leafNodeId);
            });

            const syntheticNodeId = (isIncoming ? "synthetic-in-" : "synthetic-out-") + centralNodeId;
            const dirLabel = isIncoming ? "Incoming" : "Outgoing";

            syntheticElements.push({
                group: 'nodes',
                data: {
                    id: syntheticNodeId,
                    type: 'synthetic-group',
                    label: "[ + " + prunedCount + " More ]",
                    entity_name: prunedCount + " More " + dirLabel + " Counterparties",
                    central_node_id: centralNodeId,
                    direction: isIncoming ? 'in' : 'out',
                    hidden_count: prunedCount,
                    is_synthetic: true
                },
                classes: 'synthetic-cluster'
            });

            syntheticElements.push({
                group: 'edges',
                data: {
                    id: "edge-" + syntheticNodeId,
                    source: isIncoming ? syntheticNodeId : centralNodeId,
                    target: isIncoming ? centralNodeId : syntheticNodeId,
                    edgeType: 'synthetic-link',
                    is_synthetic: true,
                    tx_count: prunedCount,
                },
                classes: 'synthetic-link'
            });
        });
    };

    processEdges(incomingLeafEdges, true);
    processEdges(outgoingLeafEdges, false);

    if (elementsToRemove.size === 0) return elements; // no pruning needed — return original ref

    const keptElements = elements.filter(el => !elementsToRemove.has(el.data.id));

    return [...keptElements, ...syntheticElements];
}
